import { createAxios } from '../lib/axiosFactory';
import { errorParser } from '../lib/response/errorParser';
import { ActionResponse } from '../lib/interfaces/actionResponse';
import { dataCollectionParser } from '../lib/response/dataParser';
import { Pages } from '../lib/enums/pages';

export const state = {
  name: '',
  description: '',
  lists: [],
  totalCount: 0,
  prevPages: [],
  currentPage: {},
  nextPages: []
};

export const mutations = {
  setLists(state: any, lists: any[]): void {
    state.lists = lists;
  },

  addList(state, list) {
    state.lists.push(list);
  },

  replaceList(state, list) {
    const listIndex = state.lists.findIndex((x: any) => x.id === list.id);
    if (listIndex >= 0) {
      state.lists.splice(listIndex, 1, list);
    }
  },

  setName(state, name) {
    state.name = name;
  },

  setDescription(state, description) {
    state.description = description;
  },

  setTotalCount(state: any, count: number): void {
    state.totalCount = count;
  },

  setPages(state: any, pageLinks: any): void {
    state.prevPages = pageLinks.previousPages;
    state.currentPage = pageLinks.currentPage;
    state.nextPages = pageLinks.nextPages;
  }
};

export const actions = {
  async loadLists(
    { commit },
    { params }: { params?: any } = {}
  ): Promise<ActionResponse> {
    try {
      let response;

      if (params) {
        response = await createAxios().get('/backend/lists-index', {
          params
        });
      } else {
        response = await createAxios().get('/backend/lists-index');
      }

      commit('setLists', dataCollectionParser(response));

      return {
        success: true,
        errors: {},
        body: dataCollectionParser(response)
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadListsWithPagination(
    { commit, rootState },
    {
      endpoint,
      searchTerm,
      sort
    }: { endpoint?: string; searchTerm?: string; sort?: string } = {}
  ): Promise<void> {
    try {
      let response;

      const pageSize: number =
        rootState.user.pageSizes[Pages.ContactLists] || 25;

      if (endpoint) {
        response = await createAxios().get(endpoint);
      } else {
        response = await createAxios().get('/backend/lists-index', {
          params: {
            'page[limit]': pageSize,
            sort: sort ?? 'createdAt:desc',
            ...(searchTerm && {
              'filter[__search:name][val]': searchTerm,
              'filter[__search:name][op]': 'search'
            })
          }
        });
      }

      const { links, meta } = response.data;
      commit('setLists', dataCollectionParser(response));
      commit('setTotalCount', meta.totalRecords);
      commit('setPages', links);
    } catch (error) {
      commit('setLists', []);
    }
  },

  async searchLists(
    { rootState },
    {
      endpoint,
      searchTerm,
      pageLimit,
      includedListIds
    }: {
      endpoint?: string;
      searchTerm?: string;
      pageLimit?: number;
      includedListIds?: string;
    } = {}
  ) {
    try {
      let response;

      const pageSize: number = pageLimit ?? rootState.user.pageSize;

      if (endpoint) {
        response = await createAxios().get(endpoint);
      } else {
        response = await createAxios().get('/backend/lists-index', {
          params: {
            'page[limit]': pageSize,
            includedListIds: includedListIds,
            ...(searchTerm && {
              'filter[__search:name][val]': searchTerm,
              'filter[__search:name][op]': 'search'
            })
          }
        });
      }
      return {
        success: true,
        errors: {},
        body: dataCollectionParser(response)
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async createList(
    { commit, rootState },
    payload: any
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post('/backend/lists-create', {
        data: {
          type: 'lists',
          attributes: payload
        }
      });

      commit('addList', {
        id: response.data.data.id,
        ...response.data.data.attributes
      });

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async updateList({ commit, rootState }, payload): Promise<ActionResponse> {
    try {
      const response = await createAxios().patch(
        '/backend/lists-update',
        {
          data: {
            id: payload.id,
            type: 'lists',
            attributes: payload
          }
        },
        {
          params: {
            id: payload.id,
            clientId: rootState.user.clientId
          }
        }
      );

      commit('replaceList', {
        id: response.data.data.id,
        ...response.data.data.attributes
      });

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async deleteList({ commit, rootState }, { listId, onlyValidate }) {
    let responseData = { success: false, message: '', errors: {} };
    try {
      const response = await createAxios().delete('/backend/lists-delete', {
        params: {
          clientId: rootState.user.clientId,
          id: listId,
          onlyValidate: onlyValidate
        }
      });

      if (response && response.status === 204) {
        responseData.success = true;
      } else {
        responseData = {
          success: false,
          message: '',
          errors: { list: 'List was not found' }
        };
      }
      return responseData;
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
